import { useEffect, useState } from 'react';

import { theme } from '@lightspeed/flame/Core/themes/flame';

const NOTIFICATION_TAB_PREFIX = 'NOTIFICATION_TAB';
const TILE_NOTIFICATION_PREFIX = 'TILE_NOTIFICATION';

/**
 * Custom event name for tile notification count changes.
 */
export const TILE_NOTIFICATION_EVENT = 'tile_notification_count_change';

/**
 * Notification style types.
 */
export const NOTIFICATION_STYLES = {
  SUCCESS: 1,
  WARNING: 2,
  DANGER: 3,
  INFO: 4,
} as const;

export type NotificationStyle = (typeof NOTIFICATION_STYLES)[keyof typeof NOTIFICATION_STYLES];

/**
 * Color configuration for each notification style.
 * Uses theme colors from Flame design system.
 */
export const NOTIFICATION_STYLE_COLORS: Record<
  NotificationStyle,
  { background: string; border: string; text: string }
> = {
  [NOTIFICATION_STYLES.SUCCESS]: {
    background: theme.colors['green-100'],
    border: theme.colors['green-300'],
    text: theme.colors.black,
  },
  [NOTIFICATION_STYLES.WARNING]: {
    background: theme.colors['orange-100'],
    border: theme.colors['orange-300'],
    text: theme.colors.black,
  },
  [NOTIFICATION_STYLES.DANGER]: {
    background: theme.colors['maple-100'],
    border: theme.colors['maple-300'],
    text: theme.colors.black,
  },
  [NOTIFICATION_STYLES.INFO]: {
    background: theme.colors['blue-100'],
    border: theme.colors['blue-300'],
    text: theme.colors.black,
  },
};

/**
 * Default cache duration in milliseconds (2 hours).
 */
export const DEFAULT_CACHE_DURATION_MS = 2 * 60 * 60 * 1000;

/**
 * Error cache duration in milliseconds (30 minutes).
 * Used when API requests fail to allow faster retry.
 */
export const ERROR_CACHE_DURATION_MS = 30 * 60 * 1000;

/**
 * Notification data structure.
 */
export interface NotificationData {
  count: number;
  style: NotificationStyle;
  updatedTimestamp?: number;
  cacheDuration?: number;
}

/**
 * Get storage key for tab notifications (used by B2B/NuOrder components).
 */
export function getStorageNotificationTabKey(tabKey: string): string {
  return `${NOTIFICATION_TAB_PREFIX}:${tabKey}`;
}

/**
 * Get localStorage key for a specific notification tile.
 */
function getNotificationStorageKey(tileKey: TileKey): string {
  return `${TILE_NOTIFICATION_PREFIX}:${tileKey}`;
}

/**
 * Get the notification data for a specific tile.
 */
export function getNotificationData(tileKey: TileKey): NotificationData {
  const data = window.localStorage.getItem(getNotificationStorageKey(tileKey));
  if (data) {
    try {
      return JSON.parse(data);
    } catch {
      return { count: 0, style: NOTIFICATION_STYLES.DANGER };
    }
  }
  return { count: 0, style: NOTIFICATION_STYLES.DANGER };
}

/**
 * Set the notification data for a specific tile and notify subscribers.
 * @param tileKey - Unique identifier for the tile
 * @param count - Number of notifications
 * @param style - Visual style for the notification badge
 * @param cacheDuration - Optional custom cache duration in milliseconds (defaults to 2 hours)
 */
export function setNotificationData(
  tileKey: TileKey,
  count: number,
  style: NotificationStyle = NOTIFICATION_STYLES.DANGER,
  cacheDuration: number = DEFAULT_CACHE_DURATION_MS
): void {
  const data: NotificationData = {
    count,
    style,
    updatedTimestamp: Date.now(),
    cacheDuration,
  };
  window.localStorage.setItem(getNotificationStorageKey(tileKey), JSON.stringify(data));
  window.dispatchEvent(new CustomEvent(TILE_NOTIFICATION_EVENT, { detail: { tileKey, ...data } }));
}

/**
 * Check if the cached notification data is still valid (within cache duration).
 * Uses the cacheDuration stored with the data, defaulting to 2 hours.
 */
export function isCacheValid(tileKey: TileKey): boolean {
  const data = getNotificationData(tileKey);
  if (!data.updatedTimestamp) {
    return false;
  }
  const duration = data.cacheDuration ?? DEFAULT_CACHE_DURATION_MS;
  return Date.now() - data.updatedTimestamp < duration;
}

/**
 * Hook to subscribe to notification changes for a specific tile.
 */
export function useNotificationData(tileKey: TileKey): NotificationData {
  const [data, setData] = useState<NotificationData>(getNotificationData(tileKey));

  useEffect(() => {
    const handleChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ tileKey: TileKey } & NotificationData>;
      if (customEvent.detail.tileKey === tileKey) {
        setData({ count: customEvent.detail.count, style: customEvent.detail.style });
      }
    };
    window.addEventListener(TILE_NOTIFICATION_EVENT, handleChange);
    return () => window.removeEventListener(TILE_NOTIFICATION_EVENT, handleChange);
  }, [tileKey]);

  return data;
}

/**
 * Tile keys for different notification sources.
 *
 * To add notifications for a new feature:
 * 1. Add a new key here (e.g., YOUR_FEATURE: 'your_feature')
 * 2. Create a data fetcher component that calls setNotificationData(TILE_KEYS.YOUR_FEATURE, count, style)
 * 3. Display the badge:
 *    - PHP: Set $option->showTileNotificationCount = true and $option->tileNotificationKey = 'your_feature'
 *    - React: Use <TileNotificationCount tileKey={TILE_KEYS.YOUR_FEATURE} /> or useNotificationData(TILE_KEYS.YOUR_FEATURE)
 */
export const TILE_KEYS = {
  BILLING_SUBSCRIPTION: 'billing_subscription',
} as const;

export type TileKey = (typeof TILE_KEYS)[keyof typeof TILE_KEYS];
